import React from "react";
import {
  Alert, Box, Button, Checkbox, Chip, FormControlLabel, MenuItem, Paper,
  Stack, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination,
  TableRow, TextField, Typography,
} from "@mui/material";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import HistoryRoundedIcon from "@mui/icons-material/HistoryRounded";
import { productsApi } from "../../../../api/products";
import { downloadPriceImportFile, priceImportCsv } from "./priceImportFiles";

const MONEY = new Intl.NumberFormat("es-AR", { style: "currency", currency: "ARS" });
const money = (value) => value === null || value === undefined ? "—" : MONEY.format(value);
const STATUS = {
  ready: ["A actualizar", "info"], updated: ["Actualizado", "success"],
  unchanged: ["Sin cambios", "default"], not_found: ["No encontrado", "warning"],
  invalid: ["Precio o código inválido", "error"], duplicate: ["Duplicado", "error"],
  ambiguous: ["Coincidencia ambigua", "error"], conflict: ["Cambió durante el proceso", "warning"], failed: ["Error", "error"],
};

function Summary({ totals = {}, committed = false }) {
  return (
    <Stack direction="row" flexWrap="wrap" gap={1}>
      {[
        ["Filas", totals.rows], [committed ? "Actualizados" : "A actualizar", totals.updated],
        ["Sin cambios", totals.unchanged], ["No encontrados", totals.notFound], ["Para revisar", totals.invalid],
      ].map(([label, value]) => (
        <Paper key={label} variant="outlined" sx={{ p: 1.5, flex: "1 1 120px", borderRadius: 2 }}>
          <Typography variant="caption" color="text.secondary">{label}</Typography>
          <Typography variant="h5" sx={{ fontWeight: 900 }}>{Number(value || 0).toLocaleString("es-AR")}</Typography>
        </Paper>
      ))}
    </Stack>
  );
}

export function PriceImportSourceExtras({ projectId, busy, onOpenHistory }) {
  const [items, setItems] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");
  const load = async () => {
    setLoading(true); setError("");
    try { setItems((await productsApi.listPriceImports(projectId))?.items || []); }
    catch (requestError) { setError(requestError.message || "No se pudo cargar el historial."); }
    finally { setLoading(false); }
  };
  return (
    <Stack spacing={1.5}>
      <Alert severity="info">Subí una lista con código y nuevo precio de venta. Los códigos desconocidos se omiten. Los nombres, categorías, costos y existencias se conservan.</Alert>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
        <Button startIcon={<DownloadRoundedIcon />} variant="outlined" disabled={busy} onClick={() => downloadPriceImportFile('codigo;precio\r\nREEMPLAZAR-POR-TU-CODIGO;12500,00', "webprofit-plantilla-precios.csv")}>Descargar plantilla</Button>
        <Button startIcon={<HistoryRoundedIcon />} variant="outlined" disabled={busy || loading} onClick={load}>{loading ? "Cargando…" : "Ver últimas actualizaciones"}</Button>
      </Stack>
      {error ? <Alert severity="error">{error}</Alert> : null}
      {items ? (
        <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
          <Typography sx={{ fontWeight: 900, mb: 1 }}>Historial de precios · últimos 90 días</Typography>
          {!items.length ? <Typography variant="body2">Todavía no hay actualizaciones por archivo.</Typography> : (
            <Stack spacing={1}>
              {items.map((item) => (
                <Stack key={item.importId} direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={1}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 800, overflowWrap: "anywhere" }}>{item.source?.fileName || "Google Sheets"}</Typography>
                    <Typography variant="caption" color="text.secondary">{new Date(item.completedAt).toLocaleString("es-AR")} · {item.totals?.updated || 0} precios actualizados</Typography>
                  </Box>
                  <Button size="small" disabled={busy} onClick={() => onOpenHistory(item.importId)}>Ver detalle y respaldo</Button>
                </Stack>
              ))}
            </Stack>
          )}
          {items.length === 20 ? <Typography variant="caption" color="text.secondary">Se muestran los últimos 20 lotes.</Typography> : null}
        </Paper>
      ) : null}
    </Stack>
  );
}

export function PriceImportMappingStep({ headers = [], mapping = {}, columnSamples = {}, allowZero, onAllowZeroChange, onChange }) {
  const sourceFor = (target) => headers.find((header) => mapping[header] === target) || "";
  const select = (target, source) => {
    const next = Object.fromEntries(headers.map((header) => [header, mapping[header] === (target === "code" ? "price" : "code") ? mapping[header] : "ignore"]));
    if (source) next[source] = target;
    onChange(next);
  };
  return (
    <Stack spacing={2}>
      <div>
        <Typography variant="h6" sx={{ fontWeight: 900 }}>Elegí sólo dos columnas</Typography>
        <Typography variant="body2" color="text.secondary">Las demás columnas quedan ignoradas. El nombre se toma del ítem existente.</Typography>
      </div>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
        {[ ["code", "Columna del código / SKU / código de barras"], ["price", "Columna del nuevo precio de venta"] ].map(([target, label]) => (
          <Box key={target} sx={{ flex: 1, minWidth: 0 }}>
            <TextField select fullWidth label={label} value={sourceFor(target)} onChange={(event) => select(target, event.target.value)}>
              <MenuItem value="">Elegir columna</MenuItem>
              {headers.map((header, index) => <MenuItem key={header} value={header} disabled={!!mapping[header] && mapping[header] !== "ignore" && mapping[header] !== target}>{index + 1}. {header}</MenuItem>)}
            </TextField>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1, overflowWrap: "anywhere" }}>
              Ejemplos: {(columnSamples[sourceFor(target)] || []).join(" · ") || "Elegí una columna para ver sus valores."}
            </Typography>
          </Box>
        ))}
      </Stack>
      <Alert severity="info">El código debe coincidir con el guardado en WebProfit. SKU y código de barras pueden ser distintos. Conservá los códigos como texto en Excel para mantener los ceros iniciales.</Alert>
      <Typography variant="body2" color="text.secondary">Usá precios como 12500,50 o 12.500,50. Una celda vacía se omite con error. Para que una variante vuelva a usar el precio base podés escribir HEREDAR.</Typography>
      <FormControlLabel control={<Checkbox checked={allowZero} onChange={(event) => onAllowZeroChange(event.target.checked)} />} label="Permitir expresamente precios en $0" />
      {allowZero ? <Alert severity="warning">Las filas con cero podrán dejar productos o variantes con precio de venta $0.</Alert> : null}
    </Stack>
  );
}

function PriceRows({ rows = [] }) {
  const [page, setPage] = React.useState(0);
  const [query, setQuery] = React.useState("");
  const [filter, setFilter] = React.useState("all");
  const visible = rows.filter((row) => {
    const matchesQuery = `${row.code} ${row.name || ""} ${row.variant || ""}`.toLocaleLowerCase("es").includes(query.toLocaleLowerCase("es"));
    return matchesQuery && (filter === "all" || (filter === "changes" ? ["ready", "updated"].includes(row.status) : !["ready", "updated", "unchanged"].includes(row.status)));
  });
  const safePage = Math.min(page, Math.max(0, Math.ceil(visible.length / 25) - 1));
  return (
    <Stack spacing={1.5}>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
        <TextField size="small" fullWidth label="Buscar código o producto" value={query} onChange={(event) => { setQuery(event.target.value); setPage(0); }} />
        <TextField select size="small" label="Mostrar" value={filter} onChange={(event) => { setFilter(event.target.value); setPage(0); }} sx={{ minWidth: 190 }}>
          <MenuItem value="all">Todas las filas</MenuItem><MenuItem value="changes">Cambios de precio</MenuItem><MenuItem value="review">Omitidos y errores</MenuItem>
        </TextField>
      </Stack>
      <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 460, borderRadius: 2 }}>
        <Table size="small" stickyHeader>
          <TableHead><TableRow>{["Fila / código", "Producto encontrado", "Precio actual / anterior", "Nuevo precio", "Diferencia", "Estado"].map((label) => <TableCell key={label} sx={{ fontWeight: 900 }}>{label}</TableCell>)}</TableRow></TableHead>
          <TableBody>
            {visible.slice(safePage * 25, safePage * 25 + 25).map((row) => {
              const [label, color] = STATUS[row.status] || [row.status, "default"];
              return (
                <TableRow key={row.row}>
                  <TableCell><Typography variant="caption" color="text.secondary">Fila {row.row}</Typography><Typography variant="body2" sx={{ fontFamily: "monospace", whiteSpace: "pre-wrap", minWidth: 100 }}>{row.code || "Sin código"}</Typography></TableCell>
                  <TableCell sx={{ minWidth: 160, maxWidth: 260, overflowWrap: "anywhere" }}>{row.name || "—"}{row.matchType === "variant" ? <Typography variant="caption" display="block" color="text.secondary">Variante: {row.variant}</Typography> : null}</TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>{money(row.previousPrice)}{row.matchType === "variant" && row.previousRawPrice === null ? <Typography variant="caption" display="block" color="text.secondary">Heredado del producto</Typography> : null}</TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap", fontWeight: 800 }}>{money(row.newPrice)}{row.matchType === "variant" && row.newRawPrice === null ? <Typography variant="caption" display="block" color="text.secondary">Usará el precio base</Typography> : null}</TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>{row.difference === undefined ? "—" : `${row.difference > 0 ? "+" : ""}${money(row.difference)}`}</TableCell>
                  <TableCell sx={{ minWidth: 190 }}><Chip size="small" label={label} color={color} /><Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>{row.message}</Typography></TableCell>
                </TableRow>
              );
            })}
            {!visible.length ? <TableRow><TableCell colSpan={6}>No hay filas para este filtro.</TableCell></TableRow> : null}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination component="div" count={visible.length} page={safePage} rowsPerPage={25} rowsPerPageOptions={[25]} onPageChange={(_, next) => setPage(next)} labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`} getItemAriaLabel={(type) => type === "next" ? "Página siguiente" : "Página anterior"} />
    </Stack>
  );
}

export function PriceImportPreviewStep({ preview }) {
  return (
    <Stack spacing={2}>
      <Typography variant="h6" sx={{ fontWeight: 900 }}>Revisá los precios antes de aplicar</Typography>
      <Summary totals={preview?.totals} />
      <Alert severity="info">Sólo se actualiza el precio del producto o de la variante identificada. Si cambiás el precio base, las variantes sin precio propio lo heredan. Los precios propios de otras variantes y las escalas por cantidad se conservan.</Alert>
      {Number(preview?.totals?.skipped) > 0 ? <Alert severity="warning">Se omitirán {preview.totals.skipped} filas, incluyendo precios sin cambios. Revisá los códigos no encontrados, duplicados o inválidos antes de continuar.</Alert> : null}
      {(preview?.blockingErrors || []).map((error) => <Alert severity="error" key={error.code}>{error.message}</Alert>)}
      <PriceRows rows={preview?.rows} />
    </Stack>
  );
}

export function PriceImportResultStep({ preview, result, busy, onCommit }) {
  if (!result) return (
    <Stack spacing={2}>
      <Typography variant="h6" sx={{ fontWeight: 900 }}>Confirmar actualización de precios</Typography>
      <Summary totals={preview?.totals} />
      <Typography>Se actualizarán {preview?.totals?.updated || 0} precios. Si algún ítem cambió desde la revisión, WebProfit te pedirá revisar nuevamente.</Typography>
      <Button variant="contained" disabled={busy || !preview?.canCommit} onClick={onCommit}>{busy ? "Actualizando…" : `Actualizar ${preview?.totals?.updated || 0} precios`}</Button>
    </Stack>
  );
  return (
    <Stack spacing={2}>
      <Typography variant="h6" sx={{ fontWeight: 900 }}>Resultado de la actualización</Typography>
      <Alert severity={result.completedWithErrors ? "warning" : "success"}>{result.globalError?.message || `Se actualizaron ${result.totals?.updated || 0} precios. ${result.totals?.skipped || 0} filas quedaron sin aplicar.`}</Alert>
      <Summary totals={result.totals} committed />
      <Typography variant="body2" color="text.secondary">Lote {result.importId} · {new Date(result.completedAt).toLocaleString("es-AR")}. {result.historySaved === false ? "Descargá el respaldo de esta pantalla; no se pudo cerrar el historial." : "El detalle y los precios anteriores quedan disponibles en el historial durante 90 días."}</Typography>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
        <Button variant="outlined" startIcon={<DownloadRoundedIcon />} onClick={() => downloadPriceImportFile(priceImportCsv(result.rows), `webprofit-precios-detalle-${result.importId}.csv`)}>Descargar detalle</Button>
        <Button variant="outlined" startIcon={<DownloadRoundedIcon />} disabled={!result.totals?.updated} onClick={() => downloadPriceImportFile(priceImportCsv(result.rows, { backup: true }), `webprofit-precios-anteriores-${result.importId}.csv`)}>Descargar precios anteriores</Button>
      </Stack>
      <Alert severity="info">Para volver a los valores anteriores, subí el archivo de respaldo en “Actualizar precios desde archivo” y revisá la propuesta. Si contiene ceros, habilitá los precios en $0. HEREDAR conserva la herencia original de las variantes.</Alert>
      <PriceRows rows={result.rows} />
    </Stack>
  );
}
